import "./card-features.scss";
import {observer} from "mobx-react";
import FontHeading4 from "../../../../../components/fonts/font-heading4/font-heading4";
import {FONT_VARIANT} from "../../../../../components/fonts/font-props";
import {Checkbox} from "../../../../../components/inputs/checkbox/checkbox";
import Card from "../../../../../components/layouts/card/card";
import {AVAILABLE_BETA_FEATURES_MAP, FEATURES, NOT_BETA_FEATURES_MAP} from "../../../../../models/Plan";
import SubscriptionPlanEditorStore from "../../../stores/subscription-plan-editor-store";
import {Input} from "../../../../../components/inputs/input/input";
import Divider from "../../../../subscription-plans/components/plan-info/divider/divider";
import {FormField} from "../../../../../components/form-field/form-field";
import { TRISTATE_CHECKBOX_VALUES, TristateCheckbox } from "../../../../../components/inputs/tristate-checkbox/tristate-checkbox";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";

interface ICardFeaturesProps {
    store?: SubscriptionPlanEditorStore;
    validationGroup: IValidationGroup;
}

const CardFeatures: React.FC<ICardFeaturesProps> = observer(({store, validationGroup}) => {

    const featureList = store?.featuresList;

    const onTristateCheckboxChange = (v: TRISTATE_CHECKBOX_VALUES) => {
        featureList?.onToggleAllFeatures(v);
    }


    return <Card
        disabled={store?.isInUse}
        toolbar={{
            title: "Features"
        }}
    >
        <div className="features-card-content">
            <TristateCheckbox
                onChange={onTristateCheckboxChange}
                label="Select all"
                value={featureList?.getAllFeaturesToggleState() ?? TRISTATE_CHECKBOX_VALUES.UNCHECKED}
                disabled={!featureList}
            />

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Account</FontHeading4>
                <div className="features-wrapper">
                    <div className="quota-based-feature">
                        <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.USERS].label}
                                  checked={featureList?.getFeature(FEATURES.USERS)?.isEnabled}
                                  disabled={true}
                        />
                        <div className="quota-based-feature-counter-wrapper">
                            <FormField
                                label={'Number of users'}
                                required={true}
                            >
                                <Input type={'number'}
                                    temp min={1}
                                    required
                                    validationGroup={validationGroup}
                                    onChange={quota => featureList?.getFeature(FEATURES.USERS)?.setQuota(quota)}
                                    value={featureList?.getFeature(FEATURES.USERS)?.quota}
                                />
                            </FormField>
                        </div>
                    </div>
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.ADDONS].label}
                              checked={featureList?.getFeature(FEATURES.ADDONS)?.isEnabled}
                              disabled={featureList?.getFeature(FEATURES.ADDONS)?.disabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.ADDONS, checked)}
                    />
                </div>
            </div>

            <Divider/>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>RMA Processing</FontHeading4>
                <div className="features-wrapper">
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.PRODUCT_EXCHANGE].label}
                              checked={featureList?.getFeature(FEATURES.PRODUCT_EXCHANGE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.PRODUCT_EXCHANGE, checked)}
                    />
                    <Checkbox
                        label={NOT_BETA_FEATURES_MAP[FEATURES.RETURN_SCORE_RESOLUTION].label}
                        checked={featureList?.getFeature(FEATURES.RETURN_SCORE_RESOLUTION)?.isEnabled}
                        onChange={checked => featureList?.toggleFeature(FEATURES.RETURN_SCORE_RESOLUTION, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.RESERVE_ITEMS].label}
                              checked={featureList?.getFeature(FEATURES.RESERVE_ITEMS)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.RESERVE_ITEMS, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.VALIDATE_ITEMS].label}
                              checked={featureList?.getFeature(FEATURES.VALIDATE_ITEMS)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.VALIDATE_ITEMS, checked)}
                    />
                </div>
            </div>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Automations</FontHeading4>
                <div className="features-wrapper">
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.AUTO_EXCHANGE].label}
                              checked={featureList?.getFeature(FEATURES.AUTO_EXCHANGE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.AUTO_EXCHANGE, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.MARK_AS_DONE].label}
                              checked={featureList?.getFeature(FEATURES.MARK_AS_DONE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.MARK_AS_DONE, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.AUTO_REFUND].label}
                              checked={featureList?.getFeature(FEATURES.AUTO_REFUND)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.AUTO_REFUND, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.CANCEL_INACTIVE_RMAS_FEATURE].label}
                              checked={featureList?.getFeature(FEATURES.CANCEL_INACTIVE_RMAS_FEATURE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.CANCEL_INACTIVE_RMAS_FEATURE, checked)}
                    />
                </div>
            </div>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Discounts</FontHeading4>
                <div className="features-wrapper">
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.BXGY].label}
                              checked={featureList?.getFeature(FEATURES.BXGY)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.BXGY, checked)}
                    />
                </div>
            </div>

            <Divider/>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Portal Enhancements</FontHeading4>
                <div className="features-wrapper">
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.INSTANT_EXCHANGE].label}
                              checked={featureList?.getFeature(FEATURES.INSTANT_EXCHANGE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.INSTANT_EXCHANGE, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.PORTAL_CHECKOUT].label}
                              checked={featureList?.getFeature(FEATURES.PORTAL_CHECKOUT)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.PORTAL_CHECKOUT, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.PORTAL_RMAS].label}
                              checked={featureList?.getFeature(FEATURES.PORTAL_RMAS)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.PORTAL_RMAS, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.WHITE_LABEL].label}
                              checked={featureList?.getFeature(FEATURES.WHITE_LABEL)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.WHITE_LABEL, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.SWITCH_TO_EXCHANGE].label}
                              checked={featureList?.getFeature(FEATURES.SWITCH_TO_EXCHANGE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.SWITCH_TO_EXCHANGE, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.SHOP_NOW].label}
                              checked={featureList?.getFeature(FEATURES.SHOP_NOW)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.SHOP_NOW, checked)}
                    />
                </div>
            </div>

            <Divider/>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Return Scenarios</FontHeading4>
                <div className="features-wrapper">
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.GIFT_RETURNS].label}
                              checked={featureList?.getFeature(FEATURES.GIFT_RETURNS)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.GIFT_RETURNS, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.PACKING_SLIP].label}
                              checked={featureList?.getFeature(FEATURES.PACKING_SLIP)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.PACKING_SLIP, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.VIDEO_UPLOAD].label}
                              checked={featureList?.getFeature(FEATURES.VIDEO_UPLOAD)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.VIDEO_UPLOAD, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.MANAGER_APPROVAL_SETTING_FEATURE].label}
                              checked={featureList?.getFeature(FEATURES.MANAGER_APPROVAL_SETTING_FEATURE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.MANAGER_APPROVAL_SETTING_FEATURE, checked)}
                    />
                </div>
            </div>

            <Divider/>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Integrations</FontHeading4>
                <div className="features-wrapper">
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.ERP_CATEGORY].label}
                              checked={featureList?.getFeature(FEATURES.ERP_CATEGORY)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.ERP_CATEGORY, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.HELPDESK].label}
                              checked={featureList?.getFeature(FEATURES.HELPDESK)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.HELPDESK, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.NOTIFICATIONS_CATEGORY].label}
                              checked={featureList?.getFeature(FEATURES.NOTIFICATIONS_CATEGORY)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.NOTIFICATIONS_CATEGORY, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.PAYMENTS_CATEGORY].label}
                              checked={featureList?.getFeature(FEATURES.PAYMENTS_CATEGORY)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.PAYMENTS_CATEGORY, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.REFUND_SERVICES_CATEGORY].label}
                              checked={featureList?.getFeature(FEATURES.REFUND_SERVICES_CATEGORY)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.REFUND_SERVICES_CATEGORY, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.SHIPPING_CATEGORY_FEATURE].label}
                              checked={featureList?.getFeature(FEATURES.SHIPPING_CATEGORY_FEATURE)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.SHIPPING_CATEGORY_FEATURE, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.WAREHOUSE_MANAGEMENT_3PL_CATEGORY].label}
                              checked={featureList?.getFeature(FEATURES.WAREHOUSE_MANAGEMENT_3PL_CATEGORY)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.WAREHOUSE_MANAGEMENT_3PL_CATEGORY, checked)}
                    />
                    <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.SHIP_BY_RETURNGO].label}
                              checked={featureList?.getFeature(FEATURES.SHIP_BY_RETURNGO)?.isEnabled}
                              onChange={checked => featureList?.toggleFeature(FEATURES.SHIP_BY_RETURNGO, checked)}
                    />
                </div>
            </div>

            <Divider/>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Other Products</FontHeading4>
                <div className="features-wrapper usage-based-features">
                    <div className="usage-based-feature-wrapper">
                        <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.ORDER_TRACKING].label}
                                  checked={featureList?.getFeature(FEATURES.ORDER_TRACKING)?.isEnabled}
                                  onChange={checked => featureList?.toggleFeature(FEATURES.ORDER_TRACKING, checked)}
                        />
                        <div className="usage-based-inputs-wrapper">
                            <FormField
                                label={'Price per excess usage'}
                                required={true}

                            >
                                <Input type={'number'} temp
                                       step={0.01}
                                       required={featureList?.getFeature(FEATURES.ORDER_TRACKING)?.isEnabled}
                                       validationGroup={validationGroup}
                                       onChange={pricePerExcess => featureList?.getFeature(FEATURES.ORDER_TRACKING)?.setPricePerExcess(pricePerExcess)}
                                       value={(featureList?.getFeature(FEATURES.ORDER_TRACKING)?.pricePerExcess)}
                                       disabled={!featureList?.getFeature(FEATURES.ORDER_TRACKING)?.isEnabled}
                                />
                            </FormField>
                            <FormField
                                label={'Quota'}
                                required={true}
                                tooltip={'Value must be greater than or equal to 0.'}
                            >
                                <Input type={'number'} min={1} temp
                                    validationGroup={validationGroup}
                                    required={featureList?.getFeature(FEATURES.ORDER_TRACKING)?.isEnabled}
                                    onChange={quota => featureList?.getFeature(FEATURES.ORDER_TRACKING)?.setQuota(quota)}
                                    value={featureList?.getFeature(FEATURES.ORDER_TRACKING)?.quota}
                                    disabled={!featureList?.getFeature(FEATURES.ORDER_TRACKING)?.isEnabled}
                                />
                            </FormField>
                        </div>
                    </div>
                </div>
            </div>

            <div className="features-category-wrapper">
                <FontHeading4 variant={FONT_VARIANT.BOLD}>Global-E features</FontHeading4>
                <div className="features-wrapper usage-based-features">
                    <div className="usage-based-feature-wrapper">
                        <Checkbox label={NOT_BETA_FEATURES_MAP[FEATURES.GLOBAL_E_SSO].label}
                                  checked={featureList?.getFeature(FEATURES.GLOBAL_E_SSO)?.isEnabled}
                                  onChange={checked => featureList?.toggleFeature(FEATURES.GLOBAL_E_SSO, checked)}
                        />
                    </div>
                </div>
            </div>

            {(featureList && featureList.availableBetaFeatures.length > 0) &&
                <>
                    <Divider />
                    <div className="features-category-wrapper">
                        <FontHeading4 variant={FONT_VARIANT.BOLD}>Available BETA Features</FontHeading4>
                        <div className="features-wrapper">
                            {featureList.availableBetaFeatures.map(betaFeature => parseInt(betaFeature)).map((betaFeature) => 
                            (
                                <Checkbox label={AVAILABLE_BETA_FEATURES_MAP[betaFeature].label}
                                    checked={featureList.getFeature(betaFeature)?.isEnabled}
                                    onChange={checked => featureList.toggleFeature(betaFeature, checked)}
                                    key={betaFeature}
                                />
                            )
                            )}                        
                        </div>
                    </div>
                </>
            }
        </div>
    </Card>
});

export default CardFeatures;
